import { useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Popper from '@material-ui/core/Popper'
import { makeStyles, Theme } from '@material-ui/core/styles'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React, { useCallback, useContext, useMemo } from 'react'
import { DialogContext } from '../DialogContext'
import { SelectionType } from './../ProductListingDialog.types'
import ProductListingIcon, {
    ProductListingIconStatusType,
    ProductListingIconType,
} from './ProductListingIcon'

const useStyles = makeStyles((theme: Theme) => ({
    oneButtonWidth: { width: 120 },
    twoButtonWidth: { width: 180 },
    popperContainer: { backgroundColor: theme.palette.common.white },
    popperHeight: { height: 35 },
    popperSeparator: { borderRight: '1px solid #F2F2F2' },
    popperHovered: {
        color: '#E69C24',
    },
    popperInitial: {
        color: '#A78966',
    },
    elementPointer: {
        cursor: 'pointer',
    },
    bodText: {
        lineHeight: '30px',
        userSelect: 'none',
    },
    deleteIcon: {
        fontSize: pxToRem(30),
        color: '#EB5757',
    },
    disabledPopper: {
        opacity: 0.5,
        cursor: 'default',
    },
}))

export interface ProductListingCardPopperProps {
    listingId?: string
    listingLevel?: ListingLevel
    isSelected: boolean
    selectionType?: SelectionType
    popperAnchorEl?: HTMLDivElement | null
    isProductBOD?: boolean
    isProductDeprecated?: boolean
    isListingManufacturerProductType?: boolean
    onAddProductToProject: () => void
    onAddProductAsBOD: () => void
    onRemoveProduct: () => void
    isFlaggedForReview?: boolean
    onAddFlagForReview: () => void
    onRemoveFlagForReview: () => void
    onDialogStateChange?: (hasActiveDialog: boolean) => void
    onOpenUnmappedDialog: (action: () => void) => void
}

const ProductListingCardPopper: React.FC<ProductListingCardPopperProps> = ({
    listingId,
    listingLevel,
    isSelected,
    selectionType,
    popperAnchorEl,
    isProductBOD,
    isProductDeprecated,
    isListingManufacturerProductType,
    onAddProductToProject,
    onAddProductAsBOD,
    onRemoveProduct,
    isFlaggedForReview,
    onAddFlagForReview,
    onRemoveFlagForReview,
    onDialogStateChange,
    onOpenUnmappedDialog,
}) => {
    const classes = useStyles()
    const Product = 'Product'
    const BOD = 'BOD'

    const { mainTabIndex, isInProject } = useContext(DialogContext)

    const { isReviewer } = useReactiveVar(userInfo)
    const isDynamicManufacturerListSelectionEnabled =
        processEnv.REACT_APP_EnableDynamicManufacturerListSelection === '1'
    const isDisabled = React.useMemo(
        () =>
            isReviewer &&
            (!isDynamicManufacturerListSelectionEnabled ||
                listingLevel !== ListingLevel.Manufacturer ||
                !isInProject),
        [
            isReviewer,
            isDynamicManufacturerListSelectionEnabled,
            listingLevel,
            isInProject,
        ]
    )

    const [iconHover, setIconHover] = React.useState<
        ProductListingIconType | undefined
    >(undefined)

    const isSelectedListingProduct = isSelected && selectionType === Product
    const isSelectedListingBOD = isSelected && selectionType === BOD
    const isFlagForReviewDisable = isReviewer
        ? isSelectedListingProduct || isSelectedListingBOD
        : false

    const handleSetIconHover = useCallback(
        (currentIconHover: ProductListingIconType | undefined) => () =>
            setIconHover(currentIconHover),
        [setIconHover]
    )

    const isApprovedBODProduct = useMemo(
        () => isProductBOD && !isProductDeprecated,
        [isProductBOD, isProductDeprecated]
    )

    if (!listingId) return null

    const handleAddRemoveToProject = useCallback(() => {
        if (isDisabled) return

        if (mainTabIndex === 1 && !isSelectedListingProduct && !isProductDeprecated) {
            onOpenUnmappedDialog(onAddProductToProject)
            return
        }

        const action = !isSelectedListingProduct && !isProductDeprecated
            ? onAddProductToProject
            : onRemoveProduct

        action?.()
    }, [
        mainTabIndex,
        isDisabled,
        isSelectedListingProduct,
        isProductDeprecated,
        onAddProductToProject,
        onRemoveProduct,
        onOpenUnmappedDialog,
    ])


    const handleBasisOfDesign = useCallback(() => {
        if (isDisabled) return

        if (mainTabIndex === 1 && !isSelectedListingBOD) {
            onOpenUnmappedDialog(onAddProductAsBOD)
            return
        }

        const action = !isSelectedListingBOD ? onAddProductAsBOD : onRemoveProduct
        action?.()
    }, [mainTabIndex, isDisabled, isSelectedListingBOD, onAddProductAsBOD, onRemoveProduct, onOpenUnmappedDialog])


    const handleFlagForReview = useCallback(() => {
        if (isFlagForReviewDisable) return

        const action = !isFlaggedForReview ? onAddFlagForReview : onRemoveFlagForReview
        action?.()
    }, [
        isFlagForReviewDisable,
        isFlaggedForReview,
        onAddFlagForReview,
        onRemoveFlagForReview,
    ])

    return (<>
        <Popper
            data-testid={`listingCardPopper${listingId}`}
            open={!!popperAnchorEl}
            anchorEl={popperAnchorEl}
            disablePortal
            keepMounted
            modifiers={{
                offset: {
                    enabled: true,
                    offset: '0, -80', // from the top of the card media
                },
            }}
        >
            <GridContainer
                className={clsx(
                    classes.popperHeight,
                    classes.popperContainer,
                    isListingManufacturerProductType
                        ? classes.oneButtonWidth
                        : isApprovedBODProduct
                            ? classes.twoButtonWidth
                            : classes.oneButtonWidth
                )}
                justifyContent="center"
            >
                {!isListingManufacturerProductType && (
                    <HoverTooltip
                        text={
                            !isSelectedListingProduct
                                ? 'Add to Project'
                                : 'Remove Product from Project'
                        }
                    >
                        <GridContainer
                            item
                            justifyContent="center"
                            alignItems="center"
                            xs={isApprovedBODProduct ? 4 : 6}
                            className={clsx(
                                classes.popperSeparator,
                                !isDisabled && classes.elementPointer
                            )}
                            direction="column"
                            onClick={handleAddRemoveToProject}
                            onKeyDown={onEnterKeyDown(handleAddRemoveToProject)}
                            tabIndex={0}
                            data-testid="dvPopperLeftSide"
                            onMouseOver={handleSetIconHover(
                                ProductListingIconType.ADD
                            )}
                            onMouseLeave={handleSetIconHover(undefined)}
                        >
                            {!isSelectedListingProduct &&
                                !isProductDeprecated ? (
                                <ProductListingIcon
                                    iconType={ProductListingIconType.ADD}
                                    iconStatusType={
                                        isDisabled
                                            ? ProductListingIconStatusType.DISABLED
                                            : iconHover ===
                                                ProductListingIconType.ADD
                                                ? ProductListingIconStatusType.HOVERED
                                                : ProductListingIconStatusType.INITIAL
                                    }
                                />
                            ) : (
                                <DeleteOutlineIcon
                                    className={clsx(
                                        classes.deleteIcon,
                                        classes.popperHeight,
                                        isDisabled && classes.disabledPopper
                                    )}
                                    data-testid={`removeProduct`}
                                />
                            )}
                        </GridContainer>
                    </HoverTooltip>
                )}

                {isApprovedBODProduct && (
                    <HoverTooltip
                        text={
                            !isSelectedListingBOD
                                ? 'Add as Basis of Design'
                                : 'Remove as Basis of Design'
                        }
                    >
                        <GridContainer
                            item
                            justifyContent="center"
                            alignItems="center"
                            xs={!!isListingManufacturerProductType ? 6 : 4}
                            className={clsx(
                                classes.popperSeparator,
                                !isDisabled && classes.elementPointer
                            )}
                            direction="column"
                            onClick={handleBasisOfDesign}
                            onKeyDown={onEnterKeyDown(handleBasisOfDesign)}
                            tabIndex={0}
                            data-testid="dvPopperRightSide"
                            onMouseOver={handleSetIconHover(
                                ProductListingIconType.BOD
                            )}
                            onMouseLeave={handleSetIconHover(undefined)}
                        >
                            {!isSelectedListingBOD ? (
                                <ProductListingIcon
                                    iconType={ProductListingIconType.BOD}
                                    iconStatusType={
                                        isDisabled
                                            ? ProductListingIconStatusType.DISABLED
                                            : iconHover ===
                                                ProductListingIconType.BOD
                                                ? ProductListingIconStatusType.HOVERED
                                                : ProductListingIconStatusType.INITIAL
                                    }
                                />
                            ) : (
                                <DeleteOutlineIcon
                                    className={clsx(
                                        classes.deleteIcon,
                                        classes.popperHeight,
                                        isDisabled && classes.disabledPopper
                                    )}
                                    data-testid={`removeBOD`}
                                />
                            )}
                        </GridContainer>
                    </HoverTooltip>
                )}

                {(!isListingManufacturerProductType ||
                    !!isApprovedBODProduct) &&
                    !isProductDeprecated && (
                        <HoverTooltip
                            text={
                                !isFlaggedForReview
                                    ? 'Flag for review'
                                    : 'Remove flag for review'
                            }
                        >
                            <GridContainer
                                item
                                justifyContent="center"
                                alignItems="center"
                                xs={
                                    !isListingManufacturerProductType &&
                                        !!isApprovedBODProduct
                                        ? 4
                                        : 6
                                }
                                className={clsx(
                                    classes.popperSeparator,
                                    !isFlagForReviewDisable &&
                                    classes.elementPointer
                                )}
                                direction="column"
                                onClick={handleFlagForReview}
                                onKeyDown={onEnterKeyDown(handleFlagForReview)}
                                tabIndex={0}
                                data-testid="dvPopperFlagForReview"
                                onMouseOver={handleSetIconHover(
                                    ProductListingIconType.FLAGFORREVIEW
                                )}
                                onMouseLeave={handleSetIconHover(undefined)}
                            >
                                {!isFlaggedForReview ? (
                                    <ProductListingIcon
                                        iconType={
                                            ProductListingIconType.FLAGFORREVIEW
                                        }
                                        iconStatusType={
                                            isFlagForReviewDisable
                                                ? ProductListingIconStatusType.DISABLED
                                                : iconHover ===
                                                    ProductListingIconType.FLAGFORREVIEW
                                                    ? ProductListingIconStatusType.HOVERED
                                                    : ProductListingIconStatusType.INITIAL
                                        }
                                    />
                                ) : (
                                    <DeleteOutlineIcon
                                        className={clsx(
                                            classes.deleteIcon,
                                            classes.popperHeight,
                                            isFlagForReviewDisable &&
                                            classes.disabledPopper
                                        )}
                                        data-testid={`removeFlagForReview`}
                                    />
                                )}
                            </GridContainer>
                        </HoverTooltip>
                    )}
            </GridContainer>
        </Popper>
    </>)
}

export default ProductListingCardPopper
